// Stage Mail-V2: useMailAccounts — fetch /api/mail-v2/accounts and map to V2 demo shape.

import { useCallback, useEffect, useState } from "react";
import { mailV2Api, type MailAccountV2DTO } from "../../../api/mail-v2";
import { mapAccount, type AccountV2View } from "../api/mappers";

type State = {
  accounts: AccountV2View[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useMailAccounts(): State {
  const [accounts, setAccounts] = useState<AccountV2View[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadSeq, setReloadSeq] = useState(0);

  const refresh = useCallback(async () => {
    setReloadSeq((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await mailV2Api.listAccounts();
        if (cancelled) return;
        setAccounts(raw.map(mapAccount));
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadSeq]);

  return { accounts, loading, error, refresh };
}

// Re-export so callers can get raw DTO if needed (used by member list).
export type { MailAccountV2DTO };
